import React, { useState } from 'react';
import proptypes from 'prop-types';
const { func, node, number, oneOfType, shape: shapeProp, string } = proptypes;
import {
  arrayOf as arrayOfType,
  bool as boolType,
  customType,
  number as numberType,
  params,
  shape,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { StoreSearchContext } from './StoreSearch.context';

const StoreSearch = ({
  children,
  pageSize = '20',
  searchCoordinates = null,
  searchInput,
  setSearchCoordinates = () => { },
  setSearchInput = () => { },
  searchSuccessCallback = () => { },
  storePodLocalizeCallback = () => { }
}) => {
  const [highlightedStoreId, setHighlightedStoreId] = useState('');
  const [services, setServices] = useState([]);
  const [showLoader, setShowLoader] = useState(false);

  const servicesArg = {
    applianceShowroom: services.includes('applianceShowroom'),
    expandedFlooringShowroom: services.includes('expandedFlooringShowroom'),
    wiFi: services.includes('wiFi'),
    keyCutting: services.includes('keyCutting'),
    loadNGo: services.includes('loadNGo'),
    propane: services.includes('propane'),
    toolRental: services.includes('toolRental'),
    penske: services.includes('penske')
  };

  const { lat = '', lng = '' } = searchCoordinates || {};
  const { data, loading, error } = useDataModel('storeSearch', {
    variables: {
      lat: lat.toString(),
      lng: lng.toString(),
      storeSearchInput: (lat && lng) ? '' : searchInput,
      pagesize: pageSize,
      storeFeaturesFilter: servicesArg
    }
  });

  const { stores, suggestedAddresses } = data?.storeSearch || {};

  const noStoresError = !stores && !loading;

  const contextValue = {
    highlightedStoreId,
    setHighlightedStoreId,
    showError: error || noStoresError,
    services,
    setSearchCoordinates,
    setSearchInput,
    setServices,
    showLoader: !data || showLoader,
    setShowLoader,
    stores,
    suggestedAddresses,
    searchSuccessCallback,
    storePodLocalizeCallback
  };

  return (
    <StoreSearchContext.Provider value={contextValue}>
      {children}
    </StoreSearchContext.Provider>
  );
};

StoreSearch.propTypes = {
  children: node.isRequired,
  pageSize: oneOfType([string, number]),
  searchCoordinates: shapeProp({
    lat: number,
    lng: number
  }),
  searchInput: string.isRequired,
  setSearchCoordinates: func,
  setSearchInput: func,
  searchSuccessCallback: func,
  storePodLocalizeCallback: func
};

StoreSearch.displayName = 'StoreSearch';

const openCloseShape = shape({
  open: stringType(),
  close: stringType()
});

StoreSearch.dataModel = {
  storeSearch: params({
    lat: stringType(),
    lng: stringType(),
    storeSearchInput: stringType(),
    pagesize: stringType(),
    storeFeaturesFilter: customType('StoreFeaturesFilter').shape({
      applianceShowroom: boolType(),
      expandedFlooringShowroom: boolType(),
      wiFi: boolType(),
      keyCutting: boolType(),
      loadNGo: boolType(),
      propane: boolType(),
      toolRental: boolType(),
      penske: boolType()
    })
  }).shape({
    stores: arrayOfType(shape({
      storeId: stringType(),
      name: stringType(),
      address: shape({
        street: stringType(),
        city: stringType(),
        state: stringType(),
        postalCode: stringType(),
        country: stringType()
      }),
      coordinates: shape({
        lat: stringType(),
        lng: stringType()
      }),
      distance: stringType(),
      services: shape({
        loadNGo: boolType(),
        propane: boolType(),
        toolRental: boolType(),
        penske: boolType(),
        keyCutting: boolType(),
        wiFi: boolType(),
        applianceShowroom: boolType(),
        expandedFlooringShowroom: boolType(),
        largeEquipment: boolType(),
        kitchenShowroom: boolType()
      }),
      storeHours: shape({
        monday: openCloseShape,
        tuesday: openCloseShape,
        wednesday: openCloseShape,
        thursday: openCloseShape,
        friday: openCloseShape,
        saturday: openCloseShape,
        sunday: openCloseShape
      }),
      storeDetailsPageLink: stringType(),
      storeType: stringType(),
      proDeskPhone: stringType(),
      phone: stringType(),
      toolRentalPhone: stringType()
    })),
    suggestedAddresses: arrayOfType(shape({
      name: stringType(),
      coordinates: shape({
        lat: numberType({ float: true }),
        lng: numberType({ float: true })
      })
    }))
  })
};

export { StoreSearch };
